import { useEffect, useState } from 'react'
import { collection, query, where, onSnapshot, orderBy } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/store/authStore'
import type { Booking } from '@/types'
import { Link } from 'react-router-dom'
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  Clock4,
  XCircle,
  CreditCard,
  ChevronRight,
  List,
  CalendarDays
} from 'lucide-react'
import CalendarView from '@/components/Bookings/CalendarView'
import LoadingState from '@/components/ui/LoadingState'
import EmptyState from '@/components/ui/EmptyState'

export default function BookingsPage() {
  const { currentOrg, user } = useAuthStore()
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (!currentOrg || !user) {
      setLoading(false)
      return
    }

    const q = query(
      collection(db, 'bookings'),
      where('orgId', '==', currentOrg.id),
      where('userId', '==', user.uid),
      orderBy('start', 'desc')
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setBookings(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking)))
      setLoading(false)
    }, (error) => {
      console.error('Erro ao escutar reservas:', error)
      setLoading(false)
    })

    return () => unsubscribe()
  }, [currentOrg?.id, user?.uid])

  const getStatusConfig = (status: Booking['status']) => {
    switch (status) {
      case 'CONFIRMED': return { label: 'Confirmado', icon: CheckCircle, className: 'bg-green-100 text-green-700 border-green-200' }
      case 'PENDING_APPROVAL': return { label: 'Em Aprovação', icon: Clock4, className: 'bg-[#001c28]/10 text-[#001c28] border-[#001c28]/20' }
      case 'PENDING_PAYMENT': return { label: 'Aguardando Pix', icon: CreditCard, className: 'bg-[#d4a94d]/10 text-[#d4a94d] border-[#d4a94d]/20' }
      case 'IN_PROGRESS': return { label: 'Em Uso', icon: Clock, className: 'bg-blue-100 text-blue-700 border-blue-200 animate-pulse' }
      case 'CANCELED': return { label: 'Cancelado', icon: XCircle, className: 'bg-red-50 text-red-700 border-red-200' }
      default: return { label: status, icon: Clock, className: 'bg-gray-100 text-gray-700 border-gray-200' }
    }
  }


  return (
    <div className="max-w-6xl w-full mx-auto p-6 space-y-8 page-enter">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b-2 border-[#001c28]/5">
        <div className="space-y-2">
          <h1 className="text-3xl font-black text-[#001c28] tracking-tighter uppercase italic leading-[0.9]">Minhas Reservas</h1>
          <p className="font-bold text-[#001c28]/80 text-sm max-w-2xl">
            Histórico e status dos seus agendamentos em <span className="text-[#001c28] font-black">{currentOrg?.name}</span>.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex bg-[#001c28]/[0.02] p-1 rounded-xl border border-[#001c28]/10">
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all uppercase tracking-widest ${viewMode === 'list'
                ? 'bg-[#001c28] text-[#d4a94d] shadow-lg shadow-[#001c28]/20'
                : 'text-[#001c28]/40 hover:bg-[#001c28]/5'
                }`}
            >
              <List className="w-3.5 h-3.5" />
              Lista
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all uppercase tracking-widest ${viewMode === 'calendar'
                ? 'bg-[#001c28] text-[#d4a94d] shadow-lg shadow-[#001c28]/20'
                : 'text-[#001c28]/40 hover:bg-[#001c28]/5'
                }`}
            >
              <CalendarDays className="w-3.5 h-3.5" />
              Calendário
            </button>
          </div>
          <Link to="/bookings/catalog" className="flex items-center space-x-2 px-6 py-3 rounded-xl shadow-lg bg-[#d4a94d] text-white hover:bg-[#001c28] transition-all transform active:scale-95 text-xs font-black uppercase tracking-widest shadow-[#d4a94d]/30 hover:shadow-[#001c28]/30">
            Nova Reserva
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="mt-8">
          <LoadingState />
        </div>
      ) : bookings.length === 0 ? (
        <div className="mt-8">
          <EmptyState
            icon={Calendar}
            title="Sem agendas por enquanto"
            description="Você ainda não realizou nenhuma solicitação de reserva."
            action={
              <Link to="/bookings/catalog" className="text-[#001c28] font-black uppercase text-xs hover:text-[#d4a94d] transition-colors tracking-widest hover:underline">
                Ver catálogo de espaços
              </Link>
            }
          />
        </div>
      ) : viewMode === 'calendar' ? (
        <div className="mt-8">
          <CalendarView bookings={bookings} />
        </div>
      ) : (
        <div className="space-y-6 mt-8">
          <div className="grid gap-4">
            {(showAll ? bookings : bookings.slice(0, 10)).map((booking) => {
              const status = getStatusConfig(booking.status)
              const StatusIcon = status.icon
              const startDate = booking.start?.toDate?.() || new Date()

              return (
                <Link
                  key={booking.id}
                  to={`/bookings/${booking.id}`}
                  className="bg-white p-6 group hover:-translate-y-1 transition-all duration-300 flex flex-col md:flex-row md:items-center justify-between relative overflow-hidden border-b-4 border-b-transparent hover:border-b-[#d4a94d] rounded-2xl border border-[#001c28]/5 shadow-sm hover:shadow-xl hover:shadow-[#001c28]/5"
                >
                  <div className="absolute -right-4 -top-4 w-28 h-28 bg-[#001c28]/5 rounded-full blur-2xl group-hover:bg-[#001c28]/10 transition-colors" />
                  <div className="relative z-10 flex items-start space-x-6">
                    <div className="w-16 h-16 rounded-xl bg-[#001c28]/5 flex flex-col items-center justify-center border-2 border-[#001c28]/10 group-hover:bg-[#001c28] group-hover:border-[#001c28] transition-colors">
                      <span className="text-[10px] font-black text-[#001c28]/60 uppercase tracking-tighter group-hover:text-[#d4a94d]">
                        {startDate.toLocaleString('pt-BR', { month: 'short' })}
                      </span>
                      <span className="text-2xl font-black text-[#001c28] group-hover:text-white">
                        {startDate.getDate()}
                      </span>
                    </div>

                    <div className="space-y-1">
                      <h4 className="font-black text-[#001c28] uppercase tracking-tight flex items-center">
                        {(booking as any).spaceName || 'Espaço'}
                        <ChevronRight className="w-4 h-4 mx-2 text-[#001c28]/30" />
                        <span className="text-[#d4a94d] text-xs">
                          {startDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </h4>
                      <div className="flex items-center text-xs text-[#001c28]/60 font-medium space-x-4">
                        <span className="flex items-center">
                          <MapPin className="w-3 h-3 mr-1" /> {currentOrg?.name}
                        </span>
                        {booking.totalPrice > 0 && (
                          <span className="font-bold text-[#001c28] italic">
                            R$ {booking.totalPrice.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="relative z-10 mt-6 md:mt-0 flex items-center space-x-3">
                    <span className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border flex items-center ${status.className}`}>
                      <StatusIcon className="w-3 h-3 mr-2" />
                      {status.label}
                    </span>
                    <div className="w-10 h-10 rounded-xl bg-[#001c28]/5 text-[#001c28] hover:bg-[#001c28] hover:text-[#d4a94d] transition-all flex items-center justify-center border border-[#001c28]/10">
                      <ChevronRight className="w-5 h-5" />
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>

          {/* Botão Ver Mais / Mostrar Menos */}
          {bookings.length > 10 && (
            <div className="flex justify-center pt-6">
              <button
                onClick={() => setShowAll(!showAll)}
                className="px-8 py-4 bg-[#001c28] text-[#d4a94d] rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-[#d4a94d] hover:text-[#001c28] transition-all shadow-lg shadow-[#001c28]/20 hover:shadow-[#d4a94d]/20 flex items-center gap-2"
              >
                {showAll ? (
                  <>
                    <ChevronRight className="w-4 h-4 rotate-90" />
                    Mostrar Menos
                  </>
                ) : (
                  <>
                    <ChevronRight className="w-4 h-4 -rotate-90" />
                    Ver Mais ({bookings.length - 10} reservas)
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
